import { Interpolation } from '@emotion/react';
import { ReactNode, RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LabeledInputSize } from './LabeledInputCommonTypes';

/**
 * usePortal
 * Dynamically creates (or reuses) a shared DOM node for React portals.
 * Returns a function that portals arbitrary React nodes to that node.
 */
export const usePortal = (id = 'react-portals') => {
  const [target, setTarget] = useState<HTMLElement | null>(null);

  useLayoutEffect(() => {
    let node = document.getElementById(id);
    if (!node) {
      node = document.createElement('div');
      node.id = id;
      document.body.appendChild(node);
    }
    setTarget(node);
    return () => {
      // optional cleanup if you want ephemeral targets
      // node.remove()
    };
  }, [id]);

  return (children: ReactNode) => target ? createPortal(children, target) : null;
};

export const calculateDropdownPosition = (containerRef: React.RefObject<HTMLDivElement | null>, size: LabeledInputSize = 'medium') => {
  if (!containerRef.current) return { top: 0, left: 0, width: 0 };

  const rect = containerRef.current.getBoundingClientRect();
  const inputContainer = containerRef.current.querySelector('[data-input-container]') as HTMLElement;
  const inputRect = inputContainer?.getBoundingClientRect() || rect;

  const spaceBelow = window.innerHeight - inputRect.bottom;
  const spaceAbove = inputRect.top;
  const dropdownHeight = 200;
  const padding = 16;

  const shouldOpenUpward = spaceBelow < dropdownHeight && spaceAbove > dropdownHeight + padding;

  const topPosition = shouldOpenUpward
    ? inputRect.top - dropdownHeight - (size === 'tiny' ? 1 : 0)
    : inputRect.bottom + (size === 'tiny' ? 1 : 0);

  return {
    top: topPosition,
    left: inputRect.left,
    width: inputRect.width,
  };
};

export interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
  viewPortHeight: number;
}

const EMPTY_RECT: Rect = { top: 0, left: 0, width: 0, height: 0, viewPortHeight: 0 };
/**
 * Returns the element's absolute viewport rect (accounting for nested scroll containers)
 * when open. Ideal for positioning popups rendered in a portal to document.body.
 */
export const useDropdownRect = (
  ref: RefObject<HTMLElement | null>,
  isOpen: boolean,
  forceUpdateCount?: number,
): Rect => {
  const [rect, setRect] = useState<Rect>(EMPTY_RECT);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!isOpen || !element) return;

    const r = element.getBoundingClientRect();
    setRect({
      top: r.top,
      left: r.left,
      width: r.width,
      height: r.height,
      viewPortHeight: window.innerHeight,
    });
  }, [isOpen, ref, forceUpdateCount]);

  return rect;
};

export const getDropDownPositionStyling = (inputAbsoluteRect: Rect, dropdownAbsoluteRect: Rect): Interpolation<any> => {
  return (inputAbsoluteRect.top + inputAbsoluteRect.height + dropdownAbsoluteRect.height > inputAbsoluteRect.viewPortHeight
    ? {
      position: 'fixed',
      bottom: 0,
      left: `${inputAbsoluteRect.left}px`,
      width: `${inputAbsoluteRect.width}px`,
    }
    : {
      position: 'fixed',
      top: `${inputAbsoluteRect.top + inputAbsoluteRect.height - 1}px`,
      left: `${inputAbsoluteRect.left}px`,
      width: `${inputAbsoluteRect.width}px`,
    });
};

export const useEverythingYouNeedForADropdown = () => {
  const [isPicklistOpen, setIsPicklistOpen] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [forceUpdateCount, setForceUpdateCount] = useState(0);
  const forceUpdate = useCallback(() => setForceUpdateCount(prev => prev + 1), []);
  const dropdownPortal = usePortal();

  const inputAbsoluteRect = useDropdownRect(containerRef, isPicklistOpen, forceUpdateCount);
  const dropdownAbsoluteRect = useDropdownRect(dropdownRef, isPicklistOpen, forceUpdateCount);

  // Close picklist when clicking outside
  useEffect(() => {
    if (!isPicklistOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const container = containerRef.current;
      const dropdown = dropdownRef.current;
      if (
        container
        && !container.contains(target)
        && dropdown
        && !dropdown.contains(target)
      ) {
        setIsPicklistOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isPicklistOpen]);

  // Reposition dropdown on scroll/resize
  useEffect(() => {
    if (isPicklistOpen) {
      window.addEventListener('scroll', forceUpdate, true);
      window.addEventListener('resize', forceUpdate);
    }

    return () => {
      window.removeEventListener('scroll', forceUpdate, true);
      window.removeEventListener('resize', forceUpdate);
    };
  }, [isPicklistOpen, forceUpdate]);

  return {
    dropdown: (render: () => ReactNode) => isPicklistOpen ? dropdownPortal(render()) : null,
    dropdownPortal,
    isPicklistOpen,
    setIsPicklistOpen,
    dropdownPositionStyling: getDropDownPositionStyling(inputAbsoluteRect, dropdownAbsoluteRect),
    inputAbsoluteRect,
    containerRef,
    dropdownRef,
    forceUpdate,
  };
};
